import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Form, FormInstance } from 'antd'
import { computePrintableWidthPx, isImageFormat } from './paperConfig'
import { PrintStyleSettings } from '../../../../../redux/context/customReports/types'

export interface SectionEntry {
  id: number
  name: string
  type: string
}

export const DEFAULT_PRINT_SETTINGS: PrintStyleSettings = {
  paperSize: 'A4',
  orientation: 'portrait',
  scale: 100,
  margin: 'medium',
  outputFormat: 'pdf',
  pageNumbers: false,
  pageBreaks: false,
  forcedBreaks: [],
  watermark: true
}

type ServerExportFn = (settings: PrintStyleSettings, forcedBreaks: Set<number>) => Promise<void>

interface PrintSettingsContextType {
  settings: PrintStyleSettings
  updateSettings: (values: Partial<PrintStyleSettings>) => void
  forcedBreaks: Set<number>
  toggleForcedBreak: (sectionId: number) => void
  sections: SectionEntry[]
  printableWidthPx: number
  counterZoom: number
  handlePrint: () => void
  handleServerExport: () => void
  serverExportLoading: boolean
  onSave: (() => void) | null
  saveLoading: boolean
  resetSettings: () => void
  form: FormInstance<PrintStyleSettings>
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const noopForm = { setFieldsValue: noop, resetFields: noop } as unknown as FormInstance<PrintStyleSettings>

const buildReadOnlyContext = (settings: PrintStyleSettings): PrintSettingsContextType => ({
  settings,
  updateSettings: noop,
  forcedBreaks: new Set(settings.forcedBreaks),
  toggleForcedBreak: noop,
  sections: [],
  printableWidthPx: computePrintableWidthPx(settings),
  counterZoom: 100 / settings.scale,
  handlePrint: noop,
  handleServerExport: noop,
  serverExportLoading: false,
  onSave: null,
  saveLoading: false,
  resetSettings: noop,
  form: noopForm
})

const PrintSettingsContext = createContext<PrintSettingsContextType>(buildReadOnlyContext(DEFAULT_PRINT_SETTINGS))

export const usePrintSettings = () => useContext(PrintSettingsContext)

interface PrintSettingsProviderProps {
  open: boolean
  sections: SectionEntry[]
  handleServerExport: ServerExportFn | null
  serverExportLoading: boolean
  initialPrintSettings?: PrintStyleSettings
  onSave: ((settings: PrintStyleSettings) => void) | null
  saveLoading: boolean
  printTitle?: string
  children: React.ReactNode
}

export const PrintSettingsProvider: React.FC<PrintSettingsProviderProps> = ({
  open,
  sections,
  handleServerExport: serverExportFn,
  serverExportLoading,
  initialPrintSettings,
  onSave,
  saveLoading,
  printTitle,
  children
}) => {
  const buildInitialSettings = useCallback(
    () => (initialPrintSettings ? { ...DEFAULT_PRINT_SETTINGS, ...initialPrintSettings } : DEFAULT_PRINT_SETTINGS),
    [initialPrintSettings]
  )

  const [settings, setSettings] = useState<PrintStyleSettings>(buildInitialSettings)
  const [form] = Form.useForm<PrintStyleSettings>()

  const resetSettings = useCallback(() => {
    const next = buildInitialSettings()
    setSettings(next)
    form.setFieldsValue(next)
  }, [buildInitialSettings, form])

  // Reset when the drawer opens, or when the report's print settings change while
  // the drawer is closed. While open, leave edits alone even if resetSettings changes
  // (e.g. report refetch) so an active editing session is never clobbered mid-flight.
  const wasOpenRef = useRef(open)
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current
    wasOpenRef.current = open

    if (justOpened || !open) {
      resetSettings()
    }
  }, [open, resetSettings])

  const updateSettings = useCallback((values: Partial<PrintStyleSettings>) => {
    setSettings(prev => ({ ...prev, ...values }))
  }, [])

  const forcedBreaks = useMemo(() => new Set(settings.forcedBreaks), [settings.forcedBreaks])

  const toggleForcedBreak = useCallback((sectionId: number) => {
    setSettings(prev => {
      const current = prev.forcedBreaks
      const next = current.includes(sectionId) ? current.filter(id => id !== sectionId) : [...current, sectionId]
      return { ...prev, forcedBreaks: next }
    })
  }, [])

  const handlePrint = useCallback(() => {
    const prev = document.title
    if (printTitle) document.title = printTitle
    window.print()
    document.title = prev
  }, [printTitle])

  const handleServerExport = useCallback(() => {
    const isImage = isImageFormat(settings.outputFormat)
    const exportSettings = isImage ? { ...settings, pageNumbers: false, pageBreaks: false } : settings
    serverExportFn?.(exportSettings, isImage ? new Set<number>() : forcedBreaks)
  }, [serverExportFn, settings, forcedBreaks])

  const printableWidthPx = useMemo(() => computePrintableWidthPx(settings), [settings])

  const counterZoom = 100 / settings.scale

  const handleSave = useCallback(() => {
    onSave?.({
      paperSize: settings.paperSize,
      orientation: settings.orientation,
      scale: settings.scale,
      margin: settings.margin,
      outputFormat: settings.outputFormat,
      pageNumbers: settings.pageNumbers,
      pageBreaks: settings.pageBreaks,
      forcedBreaks: settings.forcedBreaks,
      watermark: settings.watermark
    })
  }, [onSave, settings])

  const value = useMemo<PrintSettingsContextType>(
    () => ({
      settings,
      updateSettings,
      forcedBreaks,
      toggleForcedBreak,
      sections,
      printableWidthPx,
      counterZoom,
      handlePrint,
      handleServerExport,
      serverExportLoading,
      onSave: onSave ? handleSave : null,
      saveLoading,
      resetSettings,
      form
    }),
    [
      settings,
      forcedBreaks,
      sections,
      printableWidthPx,
      counterZoom,
      handleServerExport,
      serverExportLoading,
      handleSave,
      onSave,
      saveLoading,
      resetSettings,
      form
    ]
  )

  return <PrintSettingsContext.Provider value={value}>{children}</PrintSettingsContext.Provider>
}

export default PrintSettingsContext
